/**
 * FrostedACModal — Apple Home 風格毛玻璃 AC 控制面板
 *
 * 設計語言：
 * - 背景透明 → 依賴父容器 backdrop-filter 做毛玻璃效果
 * - 無 card、無 section 邊框 → 元件直接浮在模糊畫布上
 * - 半圓弧溫度控制 → 類似 Apple Home thermostat
 * - 綠色 tint → AC 設備專用色調
 * - 右下角 ⚙ 設定按鈕 → Device Info + Rename
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import { TemperatureChartStyle01 } from '../components/datacharts/TemperatureChartStyle01'
import './FrostedACModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const AC_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'dial', label: 'Temperature Dial', icon: 'thermostat', slots: [
    { id: 'dial-arc', label: 'Arc Stroke', editableProps: ['color', 'glow'] },
    { id: 'dial-temp', label: 'Temperature Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-room', label: 'Room Temp Text', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'mode', label: 'Mode', icon: 'ac_unit', slots: [
    { id: 'mode-bar', label: 'Mode Bar Container', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'fontSize'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'fan', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'fan-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'fan-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'temp-chart', label: 'Temp History', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = AC_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODES = [
  { id: 'cool', label: 'Cool', emoji: '❄️' },
  { id: 'heat', label: 'Heat', emoji: '🔥' },
  { id: 'dry', label: 'Dry', emoji: '💧' },
  { id: 'fan-only', label: 'Fan', emoji: '💨' },
  { id: 'auto', label: 'Auto', emoji: '🔄' },
] as const

const FAN_SPEEDS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
  { id: 'auto', label: 'Auto' },
] as const

const TEMP_MIN = 16
const TEMP_MAX = 32

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// Ring dial — uses circle + stroke-dasharray (same as mockup)
// viewBox 200x200, r=85, circumference ≈ 534
// We show 270° arc (75% of circle) → dasharray=534, dashoffset=534*0.25=133 for track
// rotate(135,100,100) starts the arc from lower-left (7 o'clock), gap at top
const RING_R = 85
const RING_CX = 100
const RING_CY = 100
const RING_CIRC = 2 * Math.PI * RING_R // ~534
const RING_ARC_RATIO = 0.75 // 270° out of 360°
const RING_TRACK_OFFSET = RING_CIRC * (1 - RING_ARC_RATIO) // gap = 25% ≈ 133

// For temperature value: offset = circ - (ratio * arcLength)
function tempToDashOffset(temp: number): number {
  const ratio = Math.max(0, Math.min(1, (temp - TEMP_MIN) / (TEMP_MAX - TEMP_MIN)))
  const arcLen = RING_CIRC * RING_ARC_RATIO // ~400
  return RING_CIRC - ratio * arcLen
}

// Dot position: angle along the arc (starts at 135°, sweeps 270° CW)
const DEG = Math.PI / 180
function tempToDotPos(temp: number): { x: number; y: number } {
  const ratio = Math.max(0, Math.min(1, (temp - TEMP_MIN) / (TEMP_MAX - TEMP_MIN)))
  const angleDeg = 135 + ratio * 270 // 135° = start, 135+270=405°≡45° = end
  const rad = angleDeg * DEG
  return {
    x: RING_CX + RING_R * Math.cos(rad),
    y: RING_CY + RING_R * Math.sin(rad),
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedACModal({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  // Parse from prop (used as initial values only)
  // Canonical key is "on" only — server already normalizes OnOff/isOn → on
  const isOn = Boolean(getAttr(deviceState, 'on'))
  const targetTemp = numAttr(deviceState, 'thermostatTemperatureSetpoint', 'targetTemperature', 'temperature') ?? 24
  const currentTemp = numAttr(deviceState, 'temperatureAmbientCelsius', 'currentTemperature', 'ambientTemperature', 'temperature')
  const currentHumidity = numAttr(deviceState, 'humidityAmbientPercent', 'humidity')
  const currentMode = strAttr(deviceState, 'thermostatMode', 'mode') || 'cool'
  const currentFanSpeed = strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed') || 'auto'
  const currentPower = numAttr(deviceState, 'currentPowerW', 'watt', 'power')

  // ---- Settings page ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    if (device.uplink_ssid) items.push({ label: 'UPLINK SSID', value: String(device.uplink_ssid) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) {
      setIsRenaming(false)
      return
    }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Local UI state (optimistic updates) — same pattern as ACModalClassic ----
  const [pendingTemp, setPendingTemp] = useState<number | null>(null)
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFan, setPendingFan] = useState<string | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayTemp = pendingTemp ?? targetTemp
  const displayMode = pendingMode ?? currentMode
  const displayFan = pendingFan ?? currentFanSpeed
  const displayPower = pendingPower ?? isOn
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Sync pending states when device responds
  useEffect(() => { if (pendingTemp !== null && pendingTemp === targetTemp) setPendingTemp(null) }, [targetTemp, pendingTemp])
  useEffect(() => { if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null) }, [currentMode, pendingMode])
  useEffect(() => { if (pendingFan !== null && pendingFan === currentFanSpeed) setPendingFan(null) }, [currentFanSpeed, pendingFan])
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])

  // ---- Internal API caller ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  // ---- Commands ----
  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower
    setPendingPower(next)
    execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const setTemp = useCallback((temp: number) => {
    if (isEditorMode) return
    const clamped = Math.max(TEMP_MIN, Math.min(TEMP_MAX, temp))
    setPendingTemp(clamped)
    execCmd('setTemperature', { temperature: clamped })
  }, [isEditorMode, execCmd])

  const setModeCmd = useCallback((mode: string) => {
    if (isEditorMode) return
    setPendingMode(mode)
    execCmd('setMode', { mode })
  }, [isEditorMode, execCmd])

  const setFanSpeedCmd = useCallback((speed: string) => {
    if (isEditorMode) return
    setPendingFan(speed)
    execCmd('setFanSpeed', { fanSpeed: speed })
  }, [isEditorMode, execCmd])

  const holdTempRef = useRef(targetTemp)
  useEffect(() => { holdTempRef.current = targetTemp }, [targetTemp])

  const startHold = useCallback((delta: number) => {
    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        const next = Math.max(TEMP_MIN, Math.min(TEMP_MAX, holdTempRef.current + delta))
        holdTempRef.current = next
        execCmd('setTemperature', { temperature: next })
      }, 200)
    }, 400)
  }, [execCmd])

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current)
    if (holdIntervalRef.current) clearInterval(holdIntervalRef.current)
    holdTimerRef.current = null
    holdIntervalRef.current = null
  }, [])

  // ---- Ring dial values ----
  const valueDashOffset = tempToDashOffset(displayTemp)
  const dotPos = tempToDotPos(displayTemp)

  // Mode label for subtitle
  const modeLabel = MODES.find(m => m.id === displayMode)?.label || displayMode
  const fanLabel = FAN_SPEEDS.find(f => f.id === displayFan)?.label || displayFan

  const stateClass = displayPower ? 'ac-frosted--on' : 'ac-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ---- Render sections ----
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'dial':
        return (
          <div key="dial" className={`ac-frosted__dial${hl('dial')}`} data-modal-section="dial">
            <svg viewBox="0 0 200 200" className="ac-frosted__ring-svg">
              <defs>
                <linearGradient id="acfGreenGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22c55e"/>
                  <stop offset="100%" stopColor="#4ade80"/>
                </linearGradient>
              </defs>
              {/* Track (270° arc, gap at top) */}
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={RING_TRACK_OFFSET}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
              />
              {/* Value arc (green) */}
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="url(#acfGreenGrad)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={displayPower ? valueDashOffset : RING_CIRC}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
              {/* Dot indicator */}
              {displayPower && (
                <circle
                  cx={dotPos.x} cy={dotPos.y} r={7}
                  fill="#fff"
                  style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}
                />
              )}
            </svg>
            <div className="ac-frosted__dial-text">
              {displayPower ? (
                <>
                  <div>
                    <span className="ac-frosted__temp-value">{displayTemp}</span>
                    <span className="ac-frosted__temp-unit">°C</span>
                  </div>
                  {currentTemp != null && (
                    <div className="ac-frosted__room-temp">室溫 {currentTemp.toFixed(1)}°C</div>
                  )}
                </>
              ) : (
                <span className="ac-frosted__temp-dash">OFF</span>
              )}
            </div>
            {/* Invisible tap zones for +/- temp */}
            {displayPower && !isEditorMode && (
              <>
                <button
                  style={{ position: 'absolute', left: 0, top: '30%', width: '35%', height: '50%', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onClick={() => setTemp(displayTemp - 1)}
                  onMouseDown={() => startHold(-1)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(-1)}
                  onTouchEnd={stopHold}
                  aria-label="Decrease temperature"
                />
                <button
                  style={{ position: 'absolute', right: 0, top: '30%', width: '35%', height: '50%', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onClick={() => setTemp(displayTemp + 1)}
                  onMouseDown={() => startHold(1)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(1)}
                  onTouchEnd={stopHold}
                  aria-label="Increase temperature"
                />
              </>
            )}
            {/* Center tap for power toggle */}
            <button
              className="ac-frosted__power-tap"
              onClick={togglePower}
              aria-label={displayPower ? 'Turn off' : 'Turn on'}
            />
          </div>
        )

      case 'mode':
        return (
          <div key="mode" className={`ac-frosted__modes${hl('mode')}`} data-modal-section="mode">
            {MODES.map(m => (
              <button
                key={m.id}
                className={`ac-frosted__mode-btn${displayMode === m.id ? ' active' : ''}`}
                onClick={() => setModeCmd(m.id)}
              >
                <span className="icon">{m.emoji}</span> {m.label}
              </button>
            ))}
          </div>
        )

      case 'fan':
        return (
          <div key="fan" className={`ac-frosted__fan${hl('fan')}`} data-modal-section="fan">
            <div className="ac-frosted__fan-label">Fan Speed</div>
            <div className="ac-frosted__fan-buttons">
              {FAN_SPEEDS.map(f => (
                <button
                  key={f.id}
                  className={`ac-frosted__fan-btn${displayFan === f.id ? ' active' : ''}`}
                  onClick={() => setFanSpeedCmd(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )

      case 'temp-chart': {
        const chartSN = device.sn?.startsWith('MOCK') ? null : (device.sn || null)
        return (
          <div key="temp-chart" className={`ac-frosted__temp-chart${hl('temp-chart')}`} data-modal-section="temp-chart">
            <TemperatureChartStyle01
              deviceSN={chartSN}
              credentials={credentials}
            />
          </div>
        )
      }

      default:
        return null
    }
  }

  return (
    <div
      className={`ac-frosted ${stateClass}`}
      data-modal-template-id="ac-frosted"
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      {/* Header */}
      <div className="ac-frosted__header" data-modal-section="_modal">
        <button className="ac-frosted__back" onClick={onClose} aria-label="Back">‹</button>
        <span className="ac-frosted__name">{displayName}</span>
        <button
          className={`ac-frosted__power-btn${displayPower ? ' ac-frosted__power-btn--on' : ''}`}
          onClick={togglePower}
          aria-label="Power"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>

      {/* Ordered sections */}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings button (bottom-right) */}
      <button
        className="ac-frosted__settings-btn"
        onClick={() => setShowSettings(true)}
        aria-label="Settings"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="ac-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="ac-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="ac-frosted__settings-header">
              <span className="ac-frosted__settings-title">設定</span>
              <button className="ac-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            {/* Rename */}
            <div className="ac-frosted__settings-section">
              <div className="ac-frosted__settings-section-title">裝置名稱</div>
              {isRenaming ? (
                <div className="ac-frosted__settings-row">
                  <input
                    ref={renameInputRef}
                    className="ac-frosted__rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename}
                    maxLength={40}
                  />
                </div>
              ) : (
                <div className="ac-frosted__settings-row">
                  <span className="ac-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>
                    {displayName}
                  </span>
                  <button className="ac-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>

            {/* Device Info */}
            <div className="ac-frosted__settings-section">
              <div className="ac-frosted__settings-section-title">裝置資訊</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="ac-frosted__settings-row">
                  <span className="ac-frosted__settings-label">{item.label}</span>
                  <span className="ac-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button
                      className="ac-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)}
                      title="Copy"
                    >
                      📋
                      {copiedField === item.label && (
                        <span className="ac-frosted__copied-toast">Copied</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
